import React, { useEffect, useRef, useState } from 'react';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { ImageCropModal } from '../ui/ImageCropModal';
import { MediaImage } from '../ui/MediaImage';
import PremiumFeatureGate from '../premium/PremiumFeatureGate';
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from '../../constants/uploadLimits';
import { prepareImageFileForUpload } from '../../utils/prepareImageFileForUpload';
import { hasNativeCapability, nativePhotoUserError, requestNativePhoto } from '../../utils/nativeShell';

type Props = {
  maintenanceId: number;
  receiptPath?: string | null;
  receiptMime?: string | null;
  onUploaded: () => void;
  onDone?: () => void;
};

/**
 * Private receipt upload for one maintenance record (Premium documents).
 */
const MaintenanceReceiptUpload: React.FC<Props> = ({
  maintenanceId,
  receiptPath,
  receiptMime,
  onUploaded,
  onDone,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [premiumLocked, setPremiumLocked] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const nativeCamera = hasNativeCapability('native_camera');

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const loadPreview = async () => {
      setPreviewUrl(null);
      setPdfAvailable(false);
      setPreviewLoading(false);
      if (!receiptPath) return;

      const isPdf =
        receiptMime === 'application/pdf' || receiptPath.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        setPdfAvailable(true);
        return;
      }

      setPreviewLoading(true);
      try {
        const headers = apiService.getAuthHeaders() as Record<string, string>;
        const res = await fetch(
          `${apiService.baseUrl}/maintenance_document.php?maintenance_id=${maintenanceId}`,
          { headers, credentials: 'include' }
        );
        if (cancelled) return;
        if (res.status === 402) {
          setPremiumLocked(true);
          setError('Private receipt storage is included with Bilt:Spec Premium.');
          return;
        }
        if (!res.ok) {
          setError('Could not load receipt preview.');
          return;
        }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setPreviewUrl(objectUrl);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load receipt preview.');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [maintenanceId, receiptPath, receiptMime]);

  const openReceipt = async () => {
    try {
      const headers = apiService.getAuthHeaders() as Record<string, string>;
      const res = await fetch(
        `${apiService.baseUrl}/maintenance_document.php?maintenance_id=${maintenanceId}`,
        { headers, credentials: 'include' }
      );
      if (res.status === 402) {
        setPremiumLocked(true);
        setError('Private receipt storage is included with Bilt:Spec Premium.');
        return;
      }
      if (!res.ok) {
        setError('Could not open receipt.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Could not open receipt.');
    }
  };

  const uploadFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      await apiService.uploadMaintenanceDocument(maintenanceId, file);
      onUploaded();
    } catch (err: unknown) {
      const coded = err as Error & { code?: string };
      if (coded.code === 'premium_required') {
        setPremiumLocked(true);
        setError('Private receipt storage is included with Bilt:Spec Premium.');
      } else {
        setError(err instanceof Error ? err.message : 'Upload failed');
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleNativeCapture = async (source: 'camera' | 'library') => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await requestNativePhoto({
        purpose: 'maintenance_receipt',
        maintenanceId,
        source,
      });
      if (!result.ok) {
        if (result.error === 'cancelled') return;
        if (result.error === 'premium_required' || /premium/i.test(String(result.error))) {
          setPremiumLocked(true);
          setError('Private receipt storage is included with Bilt:Spec Premium.');
          return;
        }
        setError(nativePhotoUserError(result.error));
        return;
      }
      onUploaded();
    } finally {
      setBusy(false);
    }
  };

  const handlePick = (file: File | undefined) => {
    if (!file || busy) return;
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
        setError(`PDF must be under ${IMAGE_UPLOAD_MAX_MB}MB.`);
        return;
      }
      void uploadFile(file);
      return;
    }

    const typeOk =
      file.type.startsWith('image/') ||
      !file.type ||
      /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
    if (!typeOk) {
      setError('Upload a JPEG, PNG, WebP image, or PDF receipt.');
      return;
    }

    void (async () => {
      setBusy(true);
      setError(null);
      try {
        const prepared = await prepareImageFileForUpload(file, {
          maxLongEdge: 2048,
          maxBytes: IMAGE_UPLOAD_MAX_BYTES,
        });
        if (prepared.size > IMAGE_UPLOAD_MAX_BYTES) {
          setError("We couldn't prepare this photo. Try another image.");
          return;
        }
        setCropFile(prepared);
        setCropOpen(true);
      } catch {
        setError("We couldn't prepare this photo. Try another image.");
      } finally {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    })();
  };

  return (
    <div className="mt-2 space-y-2">
      {previewLoading ? (
        <div className="h-28 max-w-[160px] rounded-lg border border-white/10 bg-black/30 animate-pulse" aria-hidden />
      ) : null}
      {pdfAvailable ? (
        <div className="flex items-center gap-2">
          <p className="text-xs text-zinc-400">PDF receipt on file</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void openReceipt()}>
            View PDF
          </Button>
        </div>
      ) : previewUrl ? (
        <div className="rounded-lg border border-white/10 bg-black/30 p-2 max-w-[160px]">
          <button
            type="button"
            className="block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40 rounded-md"
            onClick={() => void openReceipt()}
            aria-label="View receipt full size"
          >
            <MediaImage
              src={previewUrl}
              alt="Service receipt"
              fit="contain"
              className="w-full h-28 rounded-md bg-black/40"
            />
          </button>
          <p className="text-[10px] text-zinc-500 mt-1">Receipt on file · tap to view</p>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {nativeCamera ? (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void handleNativeCapture('camera')}
            >
              Take photo
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => void handleNativeCapture('library')}
            >
              Choose from library
            </Button>
          </>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
          className="sr-only"
          onChange={(e) => handlePick(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy
            ? cropOpen
              ? 'Uploading…'
              : 'Preparing image…'
            : receiptPath || pdfAvailable
              ? nativeCamera
                ? 'Upload PDF or file'
                : 'Replace receipt'
              : nativeCamera
                ? 'Upload PDF or file'
                : 'Add receipt'}
        </Button>
        {onDone ? (
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onDone}>
            Done
          </Button>
        ) : null}
      </div>
      {error ? (
        <p className="text-xs text-red-300" role="alert">
          {error}
        </p>
      ) : (
        <p className="text-[11px] text-zinc-500">
          Receipts are private. Only you can open them while signed in.
        </p>
      )}
      <PremiumFeatureGate
        locked={premiumLocked}
        capability="premium_documents"
        title="Private receipt storage"
        description="Attach photos or PDFs to service records. Included with Bilt:Spec Premium."
      />
      <ImageCropModal
        isOpen={cropOpen}
        file={cropFile}
        presetId="maintenance_receipt"
        busy={busy}
        onClose={() => {
          setCropOpen(false);
          setCropFile(null);
        }}
        onConfirm={async ({ file }) => {
          setCropOpen(false);
          setCropFile(null);
          await uploadFile(file);
        }}
      />
    </div>
  );
};

export default MaintenanceReceiptUpload;
